import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { ME_KEY, useMe } from './api/auth';
import { setUnauthorizedHandler } from './api/client';
import { Shell } from './components/chrome';
import { ApiErrorBanner, SkeletonBlock } from './components/states';
import { loginPath } from './lib/auth';
import { navigate, useLocation } from './lib/router';

// One chunk per screen keeps the first load small (Recharts only loads with a chart screen).
const Market = lazy(() => import('./screens/Market').then((m) => ({ default: m.Market })));
const Genres = lazy(() => import('./screens/Genres').then((m) => ({ default: m.Genres })));
const Island = lazy(() => import('./screens/Island').then((m) => ({ default: m.Island })));
const Concepts = lazy(() => import('./screens/Concepts').then((m) => ({ default: m.Concepts })));
const MyIslands = lazy(() => import('./screens/MyIslands').then((m) => ({ default: m.MyIslands })));
const Guide = lazy(() => import('./screens/Guide').then((m) => ({ default: m.Guide })));
const Settings = lazy(() => import('./screens/Settings').then((m) => ({ default: m.Settings })));
const Account = lazy(() => import('./screens/Account').then((m) => ({ default: m.Account })));
const Login = lazy(() => import('./screens/Login').then((m) => ({ default: m.Login })));

function Screen() {
  const { route } = useLocation();
  switch (route.screen) {
    case 'market':
      return <Market />;
    case 'genres':
      return <Genres />;
    case 'island':
      return <Island key={route.code ?? ''} code={route.code} />;
    case 'concepts':
      return <Concepts />;
    case 'mine':
      return <MyIslands />;
    case 'guide':
      return <Guide />;
    case 'settings':
      return route.tab === 'account' ? <Account /> : <Settings />;
    case 'login':
      return null;
  }
}

function ScreenFallback() {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <SkeletonBlock height={28} width="30%" />
      <SkeletonBlock height={220} />
    </div>
  );
}

const here = () => window.location.pathname + window.location.search;

/** Sends a signed-out browser to /login?next=<here> (a no-op when it is already there). */
function toLogin(): void {
  if (window.location.pathname === '/login') return;
  navigate(loginPath(here()), { replace: true });
}

/** Every screen but /login needs a session: ask /auth/me once, then trust 401s from any call to end it. */
function SignedIn() {
  const me = useMe();
  const signedOut = me.data === null;
  useEffect(() => {
    if (signedOut) toLogin();
  }, [signedOut]);
  if (me.error) {
    return (
      <div className="auth-wait">
        <ApiErrorBanner error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }
  if (me.isLoading || signedOut) {
    return (
      <div className="auth-wait" data-testid="auth-checking">
        <span className="muted">Checking your sign-in…</span>
      </div>
    );
  }
  return (
    <Shell>
      <Suspense fallback={<ScreenFallback />}>
        <Screen />
      </Suspense>
    </Shell>
  );
}

function Gate() {
  const { route } = useLocation();
  if (route.screen === 'login') {
    return (
      <Suspense fallback={null}>
        <Login />
      </Suspense>
    );
  }
  return <SignedIn />;
}

export function App({ client }: { client: QueryClient }) {
  useEffect(() => {
    // A 401 from any API call: the session ended (expired, signed out elsewhere, password changed).
    setUnauthorizedHandler(() => {
      client.setQueryData(ME_KEY, null);
      toLogin();
    });
  }, [client]);
  return (
    <QueryClientProvider client={client}>
      <Gate />
    </QueryClientProvider>
  );
}
